import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface KioskTypesSectionProps {
  onRegisterClick: () => void;
}

const KioskTypesSection = ({ onRegisterClick }: KioskTypesSectionProps) => {
  const { t } = useTranslation('choHem');

  const kiosks = [
    {
      name: t('kiosk.mini.name'),
      size: t('kiosk.mini.size'),
      area: t('kiosk.mini.area'),
      daily: t('kiosk.mini.daily'),
      monthly: t('kiosk.mini.monthly'),
      badge: t('kiosk.mini.badge'),
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
      icon: 'ri-store-2-line',
      features: [
        t('kiosk.features.mini'),
        t('kiosk.feature1'),
        t('kiosk.feature2'),
        t('kiosk.feature3'),
        t('kiosk.feature4'),
      ],
      highlight: false,
    },
    {
      name: t('kiosk.standard.name'),
      size: t('kiosk.standard.size'),
      area: t('kiosk.standard.area'),
      daily: t('kiosk.standard.daily'),
      monthly: t('kiosk.standard.monthly'),
      badge: t('kiosk.standard.badge'),
      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
      icon: 'ri-store-3-line',
      features: [
        t('kiosk.features.standard'),
        t('kiosk.feature1'),
        t('kiosk.feature2'),
        t('kiosk.feature3'),
        t('kiosk.feature4'),
      ],
      highlight: true,
    },
    {
      name: t('kiosk.office.name'),
      size: t('kiosk.office.size'),
      area: t('kiosk.office.area'),
      daily: t('kiosk.office.daily'),
      monthly: t('kiosk.office.monthly'),
      badge: t('kiosk.office.badge'),
      badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-400/30',
      icon: 'ri-building-4-line',
      features: [
        t('kiosk.features.office'),
        t('kiosk.feature5'),
        t('kiosk.feature6'),
        t('kiosk.feature7'),
        t('kiosk.feature8'),
      ],
      highlight: false,
    },
  ];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, offsetWidth } = scrollRef.current;
    const cardWidth = offsetWidth * 0.82 + 12;
    const idx = Math.round(scrollLeft / cardWidth);
    setActiveIndex(Math.min(idx, kiosks.length - 1));
  };

  const scrollTo = (idx: number) => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.offsetWidth * 0.82 + 12;
    scrollRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
    setActiveIndex(idx);
  };

  return (
    <section className="py-14 md:py-24 bg-gradient-to-b from-black to-gray-950" id="kiosk-types">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16">
          <span className="inline-block px-3 py-1 bg-amber-500/15 border border-amber-400/30 rounded-full text-xs text-amber-300 font-semibold uppercase tracking-wider mb-3">
            {t('kiosk.badge')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white font-serif mb-3">
            {t('kiosk.title')} <span className="text-amber-400">{t('kiosk.titleHighlight')}</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
            {t('kiosk.subtitle')}
          </p>

          {/* Location & Hours */}
          <div className="mt-5 inline-flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 bg-white/5 border border-white/10 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 w-full sm:w-auto">
            <div className="flex items-center gap-2.5 text-sm text-gray-300">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-2-line text-amber-400 text-lg"></i>
              </div>
              <span className="text-left"><span className="text-white font-semibold">{t('kiosk.location')}</span> {t('kiosk.locationValue')}</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-white/20 flex-shrink-0"></div>
            <div className="block sm:hidden h-px w-full bg-white/10"></div>
            <div className="flex items-center gap-2.5 text-sm text-gray-300">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-amber-400 text-lg"></i>
              </div>
              <span className="text-left"><span className="text-white font-semibold">{t('kiosk.hours')}</span> {t('kiosk.hoursValue')}</span>
            </div>
          </div>
        </div>

        {/* ── MOBILE: horizontal scroll cards ── */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {kiosks.map((k, i) => (
              <div
                key={i}
                className={`relative flex-shrink-0 snap-center rounded-2xl border transition-all duration-300 flex flex-col`}
                style={{ width: '82vw', minWidth: '260px' }}
              >
                {/* highlight ring */}
                <div className={`absolute inset-0 rounded-2xl pointer-events-none ${k.highlight ? 'border-2 border-amber-400/60' : 'border border-white/10'}`}></div>

                {k.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-3 py-0.5 bg-amber-500 text-black text-[10px] font-bold rounded-full whitespace-nowrap">
                      {t('kiosk.popularBadge')}
                    </span>
                  </div>
                )}

                <div className={`rounded-2xl p-4 flex flex-col h-full ${k.highlight ? 'bg-gradient-to-b from-amber-500/15 to-amber-600/5' : 'bg-white/5'}`}>
                  {/* Header row */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${k.highlight ? 'bg-amber-500/20' : 'bg-white/10'}`}>
                      <i className={`${k.icon} text-amber-400 text-xl`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`inline-block px-2 py-0.5 text-[9px] font-semibold rounded-full border mb-1 ${k.badgeColor}`}>
                        {k.badge}
                      </span>
                      <h3 className="text-white font-bold text-sm leading-tight">{k.name}</h3>
                      <div className="flex items-center gap-1 text-gray-400 text-xs mt-0.5">
                        <i className="ri-ruler-line text-amber-400 text-xs"></i>
                        <span>{k.size}{k.area ? ` (${k.area})` : ''}</span>
                      </div>
                    </div>
                  </div>

                  {/* Price block */}
                  <div className="flex items-center justify-between bg-black/30 border border-white/10 rounded-xl px-4 py-3 mb-4">
                    <div className="text-center">
                      <div className="text-amber-400 font-bold text-2xl leading-tight">{k.daily}</div>
                      {k.daily !== t('kiosk.office.daily') && <div className="text-gray-500 text-[10px] mt-0.5">{t('kiosk.perDay')}</div>}
                    </div>
                    {k.daily !== t('kiosk.office.daily') && (
                      <>
                        <div className="w-px h-8 bg-white/15"></div>
                        <div className="text-center">
                          <div className="text-white font-bold text-lg leading-tight">{k.monthly}</div>
                          <div className="text-gray-500 text-[10px] mt-0.5">{t('kiosk.perMonth')}</div>
                        </div>
                      </>
                    )}
                    {k.daily === t('kiosk.office.daily') && (
                      <div className="text-gray-400 text-xs">{t('kiosk.contact')}</div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-4 flex-1">
                    {k.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-gray-300">
                        <i className="ri-check-line text-amber-400 mt-0.5 flex-shrink-0 text-xs"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={onRegisterClick}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                      k.highlight
                        ? 'bg-amber-500 text-black hover:bg-amber-400'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    {t('kiosk.cta')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {kiosks.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === activeIndex ? 'w-6 h-2 bg-amber-400' : 'w-2 h-2 bg-white/25'
                }`}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP: 3-col grid (unchanged) ── */}
        <div className="hidden md:grid md:grid-cols-3 md:gap-6">
          {kiosks.map((k, i) => (
            <div
              key={i}
              className={`relative rounded-2xl border transition-all duration-300 md:hover:scale-[1.02] ${
                k.highlight
                  ? 'bg-gradient-to-b from-amber-500/15 to-amber-600/5 border-amber-400/40 shadow-xl shadow-amber-500/10'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {k.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-amber-500 text-black text-xs font-bold rounded-full whitespace-nowrap">
                    {t('kiosk.popularBadge')}
                  </span>
                </div>
              )}

              <div className="flex flex-col p-7 h-full">
                <div className="mb-4">
                  <span className={`inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full border ${k.badgeColor} mb-3`}>
                    {k.badge}
                  </span>
                  <h3 className="text-white font-bold text-xl mb-1">{k.name}</h3>
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <i className="ri-ruler-line text-amber-400"></i>
                    <span>{k.size}{k.area ? ` (${k.area})` : ''}</span>
                  </div>
                </div>

                <div className="mb-5 p-4 bg-black/30 rounded-xl border border-white/10">
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-4xl font-bold text-amber-400">{k.daily}</span>
                    {k.daily !== t('kiosk.office.daily') && <span className="text-gray-400 text-sm mb-1">{t('kiosk.perDay')}</span>}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {k.monthly !== t('kiosk.office.monthly')
                      ? <>{t('kiosk.or')} <span className="text-white font-semibold">{k.monthly}</span>{t('kiosk.perMonth')}</>
                      : <span className="text-gray-400">{t('kiosk.contact')}</span>
                    }
                  </div>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {k.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                      <i className="ri-check-line text-amber-400 mt-0.5 flex-shrink-0"></i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onRegisterClick}
                  className={`w-full py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    k.highlight
                      ? 'bg-amber-500 text-black hover:bg-amber-400'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  {t('kiosk.cta')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Promotional Banner */}
        <div className="mt-6 relative overflow-hidden bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-amber-500/20 border border-amber-400/40 rounded-2xl px-4 sm:px-6 py-4 sm:py-5">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-400/10 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
          <div className="relative flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-amber-500/20 border border-amber-400/40 rounded-full">
              <i className="ri-gift-line text-amber-400 text-xl sm:text-2xl"></i>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
                <span className="px-2.5 py-0.5 bg-red-500/80 text-white text-[10px] font-bold rounded-full uppercase tracking-wide whitespace-nowrap animate-pulse">
                  {t('kiosk.promo.badge')}
                </span>
                <span className="text-white font-bold text-sm md:text-base">
                  {t('kiosk.promo.period')} <span className="text-amber-400">01/03</span> {t('kiosk.promo.to')} <span className="text-amber-400">25/03</span>
                </span>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm">
                {t('kiosk.promo.desc')} <span className="text-white font-semibold">{t('kiosk.promo.months')}</span>:
              </p>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2">
                <div className="flex items-center gap-1.5 bg-black/30 border border-amber-400/30 rounded-lg px-3 py-1.5">
                  <i className="ri-calendar-line text-amber-400 text-sm"></i>
                  <span className="text-white font-bold text-sm">{t('kiosk.promo.discount1')}</span>
                  <span className="text-gray-400 text-xs">{t('kiosk.promo.discount1Type')}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-black/30 border border-amber-400/30 rounded-lg px-3 py-1.5">
                  <i className="ri-calendar-2-line text-amber-400 text-sm"></i>
                  <span className="text-white font-bold text-sm">{t('kiosk.promo.discount2')}</span>
                  <span className="text-gray-400 text-xs">{t('kiosk.promo.discount2Type')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-xs mt-5">
          {t('kiosk.note')}
        </p>
      </div>
    </section>
  );
};

export default KioskTypesSection;